import React, {useEffect, useRef, useState} from 'react';
import { Form, Select, Divider, Checkbox } from 'antd';
import GenericDialog from '@common/GenericDialog';
import axios, {prepareAxiosParamsForStruts} from "@common/axios";
import {isFormMissingValues} from "@common/utilityFunctions";

const { Option } = Select;

const ShareTestsDialog = ({open, onClose, handleDialogOpen, testIds, testsName, testName}) => {
    const [initialTeacherIds, setInitialTeacherIds] = useState([]),
        [teacherIds, setTeacherIds] = useState([]),
        [shareTeachers, setShareTeachers] = useState([]),
        [shareTestForm] = Form.useForm();

    const loadedInitialTeacherIds = useRef(false);

    useEffect(() => {
        if (!loadedInitialTeacherIds.current) {
            loadedInitialTeacherIds.current = true;
            axios.get('/controlCentre/getSchoolsTeachers.action')
                .then(response => {
                    if (response.data) {
                        setShareTeachers(response.data);
                        const teacherIds = [];
                        response.data.forEach((teacher) => {
                            teacherIds.push(teacher.userId);
                        });
                        setInitialTeacherIds(teacherIds);
                    }
                }).catch((error) => {
                console.error('Error loading school\'s teachers: ', error);
            });
        }
    }, []);

    const onShareTests = async () => {
        const fieldsValue = shareTestForm.getFieldsValue();

        if (isFormMissingValues(fieldsValue)) {
            shareTestForm.validateFields();
            return Promise.reject('Select at least one teacher.');
        } else {
            const params = {selectedTeachers: fieldsValue.teacherIds, testIds:testIds.toString()};
            await shareTests(params);
        }
    }

    const shareTests = async (params) => {
        await axios.post('/controlCentre/shareTest.action', prepareAxiosParamsForStruts(params), {
            headers: {'Content-Type': 'application/x-www-form-urlencoded'}
        })
            .then(response => {
                if (response.data) {
                    const uniqueUserIds = new Set(response.data.map(item => item.userId)).size;
                    displayToastMessage(`${getTestName()} shared with ${uniqueUserIds} others`, response.data.message, 'success', 5000 + ((response.data.length - 5) * 1000));
                } else {
                    displayToastMessage('Error sharing test/s, please contact the website admin', '', 'error', 10000);
                }
            }).catch((error) => {
                console.error(error);
            }).finally(() => {
                shareTestForm.resetFields();
                handleDialogOpen(false);
            });
    }

    const handleTeacherChange = value => {
        setTeacherIds(value);
    }

    const clearTeacherSelect = () => {
        shareTestForm.setFieldsValue({['teacherIds']: []});
        setTeacherIds([]);
    }

    const handleSelectAllTeacherIds = event => {
        if (event.target.checked) {
            setTeacherIds(initialTeacherIds);
            shareTestForm.setFieldsValue({['teacherIds']: initialTeacherIds});
        } else {
            clearTeacherSelect();
        }
    }

    const getTestName = () => {
        if(testIds.length > 1){
            return testsName.toLowerCase();
        } else {
            return testName.toLowerCase();
        }
    }

    const getDescriptionText = () => {
        if (testIds.length > 1) {
            return `Select a teacher(s) to share these ${testIds.length} ${testsName.toLowerCase()}. Teachers can edit and assign these ${testsName.toLowerCase()} for their class.`;
        }
        return `Select a teacher(s) to share ${testName.toLowerCase()}. Teachers can edit and assign this ${testName.toLowerCase()} for their class.`;
    }


    return (
        <GenericDialog
            title={`Share ${testIds.length > 1 ? testsName : testName}`}
            open={open}
            dialogWidth={400}
            onClose={() => {
                handleDialogOpen(false);
                shareTestForm.resetFields();
            }}
            buttons={[
                {
                    name: 'Cancel',
                    extraProps: { type: 'text' },
                },
                {
                    name: 'Share',
                    callback: onShareTests,
                },
            ]}
        >
            <p>{getDescriptionText()}</p>
            <Divider className={'ch-divider'} />
            <Form
                form={shareTestForm}
                className={'ch-form'}
                layout={'vertical'}
            >
                <Form.Item
                    label="Teachers"
                    name="teacherIds"
                    rules={[
                        {
                            required: true,
                            message: 'Select at least one teacher.',
                        },
                    ]}
                >
                    <Select
                        virtual={false}
                        showSearch={true}
                        popupRender={(menu) => (
                            <>
                                {menu}
                                <Divider style={{ margin: '0 0 4px 0' }} />
                                <div style={{ padding: '4px' }}>
                                    <Checkbox
                                        disabled={initialTeacherIds.length === 0}
                                        checked={initialTeacherIds.length === teacherIds.length}
                                        onChange={handleSelectAllTeacherIds}
                                    >
                                        Select all teachers
                                    </Checkbox>
                                </div>
                            </>
                        )}
                        allowClear
                        autoClearSearchValue={true}
                        onChange={handleTeacherChange}
                        mode="multiple"
                        maxTagCount="responsive"
                        filterOption={(input, option) =>
                            option.label?.toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {shareTeachers?.map((teacher) => (
                            <Select.Option
                                key={teacher.userId}
                                value={teacher.userId}
                                label={`${teacher.firstName} ${teacher.lastName}`}
                            >
                                {teacher.firstName + ' ' + teacher.lastName}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
        </GenericDialog>
    );
};

export default ShareTestsDialog;
